/*
 * PREFERENCES REDUCER
 *
 * Actions flow through here, and here we can respond when actions start, and are completed
 *
 */
import type { UnknownAction } from '@reduxjs/toolkit';

import * as actions from './preferencesActions';
import { mkPreferencesStore, type PreferencesStore } from './preferencesTypes';
import { mergeFetchedPreferences, mergeSetPreference } from './reducerFunctions';

type AsyncDispatch = (action: ReturnType<typeof actions.getPreferences>) => void;

const isActionWithAsyncDispatch = (action: UnknownAction): action is UnknownAction & { asyncDispatch: AsyncDispatch } =>
  'asyncDispatch' in action && typeof action.asyncDispatch === 'function';

export function preferencesReducer(
  state: PreferencesStore = mkPreferencesStore(),
  action: UnknownAction,
): PreferencesStore {
  switch (true) {
    // The sagas fetch the preferences on these.
    case actions.getPreferences.match(action):
    case actions.getPreferencesUser.match(action):
    case actions.getPreferencesDataset.match(action):
      return state.set('isLoading', true);

    // The fetched document is a group's complete preferences (they are not paged).
    case actions.getPreferencesSuccess.match(action):
      return mergeFetchedPreferences(state, action.payload).merge({ isLoading: false, error: null });

    case actions.getPreferencesFailure.match(action):
      return state.merge({ isLoading: false, error: action.payload });

    // Optimistic update; the success action applies the same preference again.
    case actions.setPreference.match(action):
    case actions.setPreferenceSuccess.match(action):
      return mergeSetPreference(state, action.payload);

    // The optimistic update may be wrong now, so refetch both groups.
    case actions.setPreferenceFailure.match(action):
      if (isActionWithAsyncDispatch(action)) {
        action.asyncDispatch(actions.getPreferences({ section: 'shared', group: 'dataset' }));
        action.asyncDispatch(actions.getPreferences({ section: 'shared', group: 'user' }));
      }
      return state;

    default:
      return state;
  }
}

export default preferencesReducer;
